import { useCallback, useMemo, useRef, useState } from "react";
import useWorkspace from "@/hooks/useWorkspace";
import { useDocumentIndexingRuns } from "@/hooks/useDocumentIndexingRuns";
import { useIndexingOperationsStore } from "@/stores/indexing-operations.store";
import { logger } from "@/lib/logger";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";

/** How many reruns one click on the notice starts; the rest wait for the next click. */
export const STALE_REINDEX_BATCH_SIZE = 20;

/** The index facts a listed document carries, whatever list it came from. */
export interface StaleIndexDocument {
  id: string;
  workspaceId: string;
  searchIndexStale: boolean;
  /** The reader may rerun kildeklargøring on it (owner or write access, not a parent workspace). */
  canReindex: boolean;
}

interface UseStaleSearchIndexOptions {
  /** False when the surface already polls its runs (the files tab); the store still reports them as running. */
  trackRuns?: boolean;
  /** Runs another poller on the same surface knows about. */
  indexingDocumentIds?: ReadonlySet<string>;
}

/**
 * One rerun flow for every list that can hold documents with an outdated search index
 * (LAR-235): the Workspaces page content search, the files tab and global search. It
 * counts the stale documents, knows which of them the reader may rerun, starts
 * kildeklargøring one by one or a batch at a time, and reports which rows should spin.
 * The same mutation as the files tab's context-menu action does the work, so a run
 * started here is tracked and finalised exactly like one started there.
 */
export function useStaleSearchIndex<T extends StaleIndexDocument>(
  documents: T[],
  {
    trackRuns = true,
    indexingDocumentIds: externalIds,
  }: UseStaleSearchIndexOptions = {}
) {
  const { reindexDocument } = useWorkspace();
  const startedOperations = useIndexingOperationsStore(
    (state) => state.operations
  );
  // Documents whose rerun has been asked for but not yet answered. The operations store
  // only learns about a run once the call returns, so without this a second click (or a
  // double click) would start the whole batch again.
  const requestedRef = useRef<Set<string>>(new Set());
  const [requestedIds, setRequestedIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const { indexingDocumentIds: trackedIds } = useDocumentIndexingRuns(
    documents,
    { enabled: trackRuns }
  );

  const indexingDocumentIds = useMemo(() => {
    const ids = new Set<string>(trackedIds);
    externalIds?.forEach((id) => ids.add(id));
    requestedIds.forEach((id) => ids.add(id));
    for (const document of documents) {
      if (document.id in startedOperations) ids.add(document.id);
    }
    return ids;
  }, [trackedIds, externalIds, requestedIds, documents, startedOperations]);

  // mutateAsync, not mutate: per-call callbacks of mutate only fire for the latest call,
  // so a batch would lose every error but the last one.
  const reindex = useCallback(
    (document: StaleIndexDocument) => {
      if (requestedRef.current.has(document.id)) return;
      requestedRef.current.add(document.id);
      setRequestedIds(new Set(requestedRef.current));
      void reindexDocument
        .mutateAsync({
          workspaceId: document.workspaceId,
          documentId: document.id,
        })
        .then((result) => {
          // An accepted background run is tracked by the poller; the instant path
          // (LAR-252 heal) is done right here and the lists refetch.
          if (result.status === "completed") {
            showSuccessToast("Kildeklargøring er fuldført.");
          }
        })
        .catch((error: unknown) => {
          logger.error(
            "Failed to start kildeklargøring from a search surface",
            {
              operation: "staleSearchIndexReindex",
              error,
              workspaceId: document.workspaceId,
              documentId: document.id,
            }
          );
          showErrorToast(
            "Kildeklargøring kunne ikke startes. Prøv igen om et øjeblik."
          );
        })
        .finally(() => {
          // An accepted run is in the operations store from here on, so the row keeps
          // its spinner; a failed one goes back to offering the action.
          requestedRef.current.delete(document.id);
          setRequestedIds(new Set(requestedRef.current));
        });
    },
    [reindexDocument]
  );

  const stale = useMemo(
    () => documents.filter((document) => document.searchIndexStale),
    [documents]
  );
  const running = useMemo(
    () => stale.filter((document) => indexingDocumentIds.has(document.id)),
    [stale, indexingDocumentIds]
  );
  const reindexable = useMemo(
    () =>
      stale.filter(
        (document) =>
          document.canReindex && !indexingDocumentIds.has(document.id)
      ),
    [stale, indexingDocumentIds]
  );
  const reindexAll = useCallback(() => {
    reindexable.slice(0, STALE_REINDEX_BATCH_SIZE).forEach(reindex);
  }, [reindexable, reindex]);

  return {
    indexingDocumentIds,
    reindex,
    reindexAll,
    staleCount: stale.length,
    runningCount: running.length,
    reindexableCount: reindexable.length,
    batchSize: STALE_REINDEX_BATCH_SIZE,
  };
}

export default useStaleSearchIndex;
